import { addDays } from "@/modules/recursos/equipe-obra.rules";
import type { ObraPeriodoOpcao } from "@/modules/obras/obras.types";

/** Linha da tabela de períodos (HU 2523); datas e consolidados são derivados, não guardados. */
export interface PeriodoLinha {
  /** Identidade estável da linha (troca de tipo e Desfazer mantêm posição e dados). */
  uid: string;
  periodoObraId: string | null;
  /** Dias corridos (inteiro ≥ 1); `null` = ainda não informado. */
  duracao: number | null;
  /** Dias, opcional (inteiro ≥ 0). */
  meta: number | null;
}

/** Snapshot da Etapa 2 em `state.etapas.duracao`. `inicioObra` em `AAAA-MM-DD` ou vazio. */
export interface DadosDuracao {
  inicioObra: string;
  periodos: PeriodoLinha[];
}

export const DURACAO_INICIAL: DadosDuracao = { inicioObra: "", periodos: [] };

export const DURACAO_MAX_DIGITOS = 8;

export const DURACAO_INICIO_OBRIGATORIO = "Informe o início da obra.";
export const DURACAO_SEM_PERIODO = "Adicione ao menos um período.";
export const DURACAO_DIAS_OBRIGATORIO = "Informe a duração.";
export const DURACAO_DIAS_MINIMO = "A duração deve ser de no mínimo 1 dia.";

/** Chave de erro por linha: `periodo.<uid>.duracao`. O período nasce escolhido no menu de "Adicionar Período". */
export const chaveErroPeriodo = (uid: string, campo: "duracao") => `periodo.${uid}.${campo}`;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isLinha(raw: unknown): raw is PeriodoLinha {
  if (typeof raw !== "object" || raw === null) return false;
  const linha = raw as Record<string, unknown>;
  return typeof linha.uid === "string";
}

export const isDuracaoVazia = (dados: DadosDuracao) =>
  dados.inicioObra === "" && dados.periodos.length === 0;

/** Lê o snapshot guardado (`unknown` no store); formato inesperado vira o estado vazio. */
export function readDuracao(raw: unknown): DadosDuracao {
  if (typeof raw !== "object" || raw === null) return DURACAO_INICIAL;

  const { inicioObra, periodos } = raw as Partial<Record<keyof DadosDuracao, unknown>>;

  return {
    inicioObra: typeof inicioObra === "string" && ISO_DATE.test(inicioObra) ? inicioObra : "",
    periodos: Array.isArray(periodos) ? periodos.filter(isLinha) : [],
  };
}

/** Duração válida = inteiro ≥ 1 (pendência 6 da HU: sem outros limites). */
export const isDuracaoValida = (duracao: number | null): duracao is number =>
  duracao !== null && Number.isInteger(duracao) && duracao >= 1;

/**
 * Erros da etapa (RN11/AC10): Início da Obra, ≥ 1 período e a Duração de cada período
 * válida. Meta é opcional e, digitada só com números, nunca bloqueia.
 */
export function validateEtapaDuracao(dados: DadosDuracao): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!dados.inicioObra) errors.inicioObra = DURACAO_INICIO_OBRIGATORIO;
  if (dados.periodos.length === 0) errors.periodos = DURACAO_SEM_PERIODO;

  for (const linha of dados.periodos) {
    if (linha.duracao === null) {
      errors[chaveErroPeriodo(linha.uid, "duracao")] = DURACAO_DIAS_OBRIGATORIO;
    } else if (!isDuracaoValida(linha.duracao)) {
      errors[chaveErroPeriodo(linha.uid, "duracao")] = DURACAO_DIAS_MINIMO;
    }
  }

  return errors;
}

export interface PeriodoCalculado {
  uid: string;
  /** `AAAA-MM-DD`; `null` sem Início da Obra. */
  inicio: string | null;
  /** `null` sem Início da Obra ou sem duração válida. */
  fim: string | null;
}

export interface CronogramaDuracao {
  linhas: PeriodoCalculado[];
  /** Início da Obra, ou `null` se vazio. */
  inicio: string | null;
  /** Fim do último período com duração válida (Término previsto). */
  terminoPrevisto: string | null;
  /** Soma das durações válidas = término − início + 1. */
  duracaoTotal: number | null;
}

/**
 * Fórmulas da HU (RN06, dias corridos, contagem inclusiva): início do 1º = Início da Obra;
 * fim = início + duração − 1; início do seguinte = fim anterior + 1. Uma linha sem duração
 * válida ainda não consome dias: o seguinte começa no mesmo dia.
 */
export function calcularCronograma(dados: DadosDuracao): CronogramaDuracao {
  const inicio = dados.inicioObra || null;
  const linhas: PeriodoCalculado[] = [];

  let cursor = inicio;
  let terminoPrevisto: string | null = null;
  let duracaoTotal = 0;

  for (const linha of dados.periodos) {
    if (cursor === null) {
      linhas.push({ uid: linha.uid, inicio: null, fim: null });
      continue;
    }

    if (!isDuracaoValida(linha.duracao)) {
      linhas.push({ uid: linha.uid, inicio: cursor, fim: null });
      continue;
    }

    const fim = addDays(cursor, linha.duracao - 1);
    linhas.push({ uid: linha.uid, inicio: cursor, fim });
    terminoPrevisto = fim;
    duracaoTotal += linha.duracao;
    cursor = addDays(fim, 1);
  }

  return {
    linhas,
    inicio,
    terminoPrevisto,
    duracaoTotal: terminoPrevisto === null ? null : duracaoTotal,
  };
}

/** Intervalo dos períodos classificados como Obra (D-110); `null` se não há nenhum calculável. */
export function intervaloPeriodoObra(
  dados: DadosDuracao,
  cronograma: CronogramaDuracao,
  catalogo: readonly ObraPeriodoOpcao[],
): { inicio: string; fim: string } | null {
  let inicio: string | null = null;
  let fim: string | null = null;

  dados.periodos.forEach((linha, index) => {
    const tipo = catalogo.find((item) => item.id === linha.periodoObraId);
    const calculado = cronograma.linhas[index];
    if (tipo?.etapa !== "OBRA" || !calculado?.inicio || !calculado.fim) return;

    inicio ??= calculado.inicio;
    fim = calculado.fim;
  });

  return inicio !== null && fim !== null ? { inicio, fim } : null;
}

/** Há ao menos um período classificado como Obra (mesmo sem duração)? */
export function temPeriodoObra(
  dados: DadosDuracao,
  catalogo: readonly ObraPeriodoOpcao[],
): boolean {
  return dados.periodos.some(
    (linha) => catalogo.find((item) => item.id === linha.periodoObraId)?.etapa === "OBRA",
  );
}

export type SeveridadeLimite = "danger" | "warn" | "secondary";

/**
 * Severidade da tag de Limite de Classe nesta etapa (Figma, "Momento" Sem/Com período).
 * Sem período da obra: vencido → warn, em dia → secondary. Com período da obra: vence antes do
 * início da obra → danger; até 30 dias depois do fim da obra → warn; senão secondary.
 */
export function severidadeLimite(
  limite: { vencimento: string; vencido: boolean },
  periodoObra: { inicio: string; fim: string } | null,
): SeveridadeLimite {
  if (!periodoObra) return limite.vencido ? "warn" : "secondary";
  if (!ISO_DATE.test(limite.vencimento)) return "secondary";
  if (limite.vencimento < periodoObra.inicio) return "danger";
  if (limite.vencimento <= addDays(periodoObra.fim, 30)) return "warn";
  return "secondary";
}
